import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Shield, Clock, Calendar, ArrowLeft, ArrowRight, Share2, BookOpen, CheckCircle, AlertTriangle, Info } from 'lucide-react';
import { getArticleBySlug, getRelatedArticles } from '@/data/blogArticles';
import { Helmet } from 'react-helmet-async';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Header from '@/components/Header';

const ArticlePage = () => {
  const { slug } = useParams();
  const article = getArticleBySlug(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#f8fafc' }}>
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Article non trouve</h1>
          <Link to="/blog" className="text-blue-600 hover:underline">
            Retour au blog
          </Link>
        </div>
      </div>
    );
  }

  const relatedArticles = getRelatedArticles(article.id);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: article.title,
          text: article.excerpt,
          url: url
        });
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      navigator.clipboard.writeText(url);
      alert('Lien copie dans le presse-papier !');
    }
  };

  // Schema.org Article structured data
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.metaDescription,
    "image": "https://www.anti-recouvrement.com/og-image.png",
    "author": {
      "@type": "Organization",
      "name": "Anti-Recouvrement"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Anti-Recouvrement",
      "logo": {
        "@type": "ImageObject",
        "url": "https://www.anti-recouvrement.com/logo.png"
      }
    },
    "datePublished": article.publishedAt,
    "dateModified": article.updatedAt,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://www.anti-recouvrement.com/blog/${article.slug}`
    }
  };

  // Schema.org BreadcrumbList — affichage du fil d'Ariane dans les SERP Google
  // Accueil → Blog → [Titre de l'article]
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Accueil",
        "item": "https://www.anti-recouvrement.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Blog",
        "item": "https://www.anti-recouvrement.com/blog"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": article.title,
        "item": `https://www.anti-recouvrement.com/blog/${article.slug}`
      }
    ]
  };

  // Custom components for ReactMarkdown
  const markdownComponents = {
    h2: ({ children }) => (
      <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-16 mb-6 pb-3 border-b-2 border-blue-100">
        {children}
      </h2>
    ),
    h3: ({ children }) => (
      <h3 className="text-xl md:text-2xl font-semibold text-gray-800 mt-10 mb-4">
        {children}
      </h3>
    ),
    p: ({ children }) => (
      <p className="text-gray-700 text-lg leading-relaxed mb-6">
        {children}
      </p>
    ),
    ul: ({ children }) => (
      <ul className="my-6 space-y-3 pl-2">
        {children}
      </ul>
    ),
    ol: ({ children }) => (
      <ol className="my-6 space-y-3 pl-2 list-decimal list-inside">
        {children}
      </ol>
    ),
    li: ({ children }) => (
      <li className="text-gray-700 text-lg leading-relaxed flex items-start gap-3">
        <span className="text-blue-600 mt-1.5 flex-shrink-0">•</span>
        <span>{children}</span>
      </li>
    ),
    strong: ({ children }) => (
      <strong className="font-semibold text-gray-900">{children}</strong>
    ),
    blockquote: ({ children }) => (
      <blockquote className="my-8 p-6 bg-blue-50 border-l-4 border-blue-500 rounded-r-lg">
        <div className="flex items-start gap-3">
          <Info className="text-blue-600 flex-shrink-0 mt-1" size={20} />
          <div className="text-gray-700 italic text-lg">{children}</div>
        </div>
      </blockquote>
    ),
    table: ({ children }) => (
      <div className="my-8 overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
        <table className="w-full">
          {children}
        </table>
      </div>
    ),
    thead: ({ children }) => (
      <thead className="bg-gray-50 border-b border-gray-200">
        {children}
      </thead>
    ),
    th: ({ children }) => (
      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
        {children}
      </th>
    ),
    td: ({ children }) => (
      <td className="px-6 py-4 text-gray-700 border-b border-gray-100">
        {children}
      </td>
    ),
    a: ({ href, children }) => (
      <a
        href={href}
        className="text-blue-600 font-medium hover:text-blue-800 underline decoration-blue-300 hover:decoration-blue-600 transition-colors"
      >
        {children}
      </a>
    ),
    code: ({ children }) => (
      <code className="px-2 py-1 bg-gray-100 rounded text-sm font-mono text-gray-800">
        {children}
      </code>
    ),
  };

  return (
    <>
      <Helmet>
        <title>{article.metaTitle}</title>
        <meta name="description" content={article.metaDescription} />
        <meta name="keywords" content={article.keyword} />
        <meta property="og:title" content={article.metaTitle} />
        <meta property="og:description" content={article.metaDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://www.anti-recouvrement.com/blog/${article.slug}`} />
        <meta property="article:published_time" content={article.publishedAt} />
        <meta property="article:modified_time" content={article.updatedAt} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={article.metaTitle} />
        <meta name="twitter:description" content={article.metaDescription} />
        <link rel="canonical" href={`https://www.anti-recouvrement.com/blog/${article.slug}`} />
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen" style={{ backgroundColor: '#f8fafc' }}>
        <Header showAnalyzeCta />

        {/* Hero Section */}
        <div className="bg-gradient-to-b from-blue-900 to-blue-800 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm text-blue-200 mb-8">
              <Link to="/" className="hover:text-white transition-colors">Accueil</Link>
              <span>/</span>
              <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
              <span>/</span>
              <span className="text-white truncate">{article.category}</span>
            </nav>

            {/* Article Header */}
            <div className="flex items-center gap-3 mb-6">
              <span className="inline-block px-4 py-1.5 text-sm font-medium rounded-full bg-white/20 text-white">
                {article.category}
              </span>
              {article.isPillar && (
                <span className="inline-block px-4 py-1.5 text-sm font-medium rounded-full bg-green-500/30 text-green-100">
                  Guide Principal
                </span>
              )}
            </div>

            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 leading-tight">
              {article.title}
            </h1>

            <p className="text-xl text-blue-100 mb-8 leading-relaxed max-w-3xl">
              {article.excerpt}
            </p>

            <div className="flex flex-wrap items-center gap-6 text-sm text-blue-200">
              <span className="flex items-center gap-2">
                <Clock size={18} />
                {article.readTime} min de lecture
              </span>
              <span className="flex items-center gap-2">
                <Calendar size={18} />
                {formatDate(article.publishedAt)}
              </span>
              <button
                onClick={handleShare}
                className="flex items-center gap-2 ml-auto px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
              >
                <Share2 size={18} />
                Partager
              </button>
            </div>
          </div>
        </div>

        {/* Article Content */}
        <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
          {/* Key Takeaways Box */}
          <div className="bg-green-50 border border-green-200 rounded-2xl p-6 md:p-8 mb-12">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="text-green-600" size={24} />
              </div>
              <h2 className="text-xl font-bold text-green-900">Points cles de cet article</h2>
            </div>
            <ul className="space-y-3">
              <li className="flex items-start gap-3 text-green-800">
                <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={18} />
                <span>Comprendre les delais de prescription selon le type de dette</span>
              </li>
              <li className="flex items-start gap-3 text-green-800">
                <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={18} />
                <span>Identifier les actes qui interrompent la prescription</span>
              </li>
              <li className="flex items-start gap-3 text-green-800">
                <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={18} />
                <span>Savoir comment invoquer la prescription de maniere efficace</span>
              </li>
            </ul>
          </div>

          {/* Article Body with Custom Styling */}
          <div className="article-content">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={markdownComponents}
            >
              {article.content}
            </ReactMarkdown>
          </div>

          {/* Warning Box */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 md:p-8 mt-12">
            <div className="flex items-start gap-4">
              <div className="p-2 bg-amber-100 rounded-lg flex-shrink-0">
                <AlertTriangle className="text-amber-600" size={24} />
              </div>
              <div>
                <h3 className="text-lg font-bold text-amber-900 mb-2">Important</h3>
                <p className="text-amber-800 leading-relaxed">
                  Cet article a un but informatif et ne constitue pas un conseil juridique.
                  Chaque situation est unique. Pour un cas specifique, consultez un professionnel du droit.
                </p>
              </div>
            </div>
          </div>

          {/* CTA Box */}
          <div className="mt-16 rounded-3xl overflow-hidden shadow-xl">
            <div className="bg-gradient-to-r from-blue-900 to-blue-700 p-8 md:p-12 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 rounded-full mb-6">
                <Shield className="text-white" size={32} />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
                Verifiez gratuitement si votre dette est prescrite
              </h2>
              <p className="text-blue-100 text-lg mb-8 max-w-2xl mx-auto leading-relaxed">
                Notre outil analyse votre courrier de recouvrement en quelques minutes
                et vous donne un score de probabilite avec des recommandations personnalisees.
              </p>
              <Link
                to="/"
                className="inline-block px-10 py-4 bg-white rounded-xl font-bold text-lg transition-all hover:scale-105 hover:shadow-lg"
                style={{ color: '#1e3a8a' }}
              >
                Analyser ma dette maintenant
              </Link>
              <p className="text-blue-200 text-sm mt-4">
                Analyse 100% gratuite - Sans engagement
              </p>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="bg-white py-16">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-2xl md:text-3xl font-bold mb-8" style={{ color: '#1e3a8a' }}>
                Articles connexes
              </h2>
              <div className="grid md:grid-cols-3 gap-8">
                {relatedArticles.slice(0, 3).map((related) => (
                  <Link key={related.id} to={`/blog/${related.slug}`}>
                    <article className="bg-gray-50 rounded-2xl p-6 h-full hover:shadow-lg transition-all duration-300 hover:-translate-y-1 border border-gray-100">
                      <span className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700 mb-4">
                        {related.category}
                      </span>
                      <h3 className="text-lg font-bold text-gray-900 mb-3 line-clamp-2 hover:text-blue-800 transition-colors">
                        {related.title}
                      </h3>
                      <p className="text-gray-600 text-sm mb-4 line-clamp-3 leading-relaxed">
                        {related.excerpt}
                      </p>
                      <span className="flex items-center gap-2 text-sm font-semibold" style={{ color: '#1e3a8a' }}>
                        Lire l'article
                        <ArrowRight size={16} />
                      </span>
                    </article>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Back to Blog */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors font-medium"
          >
            <ArrowLeft size={20} />
            Retour a tous les articles
          </Link>
        </div>

        {/* Footer */}
        <footer className="bg-gray-900 text-white py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-3">
                <Shield size={28} />
                <span className="font-bold text-lg">Anti-Recouvrement</span>
              </div>
              <nav className="flex items-center gap-8 text-sm text-gray-400">
                <Link to="/" className="hover:text-white transition-colors">Accueil</Link>
                <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
                <Link to="/login" className="hover:text-white transition-colors">Connexion</Link>
              </nav>
              <p className="text-sm text-gray-500">
                2025 Anti-Recouvrement. Tous droits reserves.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default ArticlePage;
